import { useState } from "react";
import { motion } from "framer-motion";
import { Plane, AlertTriangle, CheckCircle, Cloud, Wind, Eye, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RiskBadge = ({ risk }) => {
  const styles = {
    low: "bg-green-500/20 text-green-400 border-green-500/30",
    moderate: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    high: "bg-red-500/20 text-red-400 border-red-500/30",
    unknown: "bg-gray-500/20 text-gray-400 border-gray-500/30"
  };
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${styles[risk] || styles.unknown}`}>
      {risk?.toUpperCase() || "UNKNOWN"}
    </span>
  );
};

const AirportCard = ({ title, data, type, compact = false }) => {
  if (!data) return null;
  
  return (
    <div className={`glass-light rounded-xl ${compact ? 'p-3' : 'p-4'} flex-1`} data-testid={`flight-airport-${type}-card`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <span className="text-sm text-white/60">{title}</span>
        <RiskBadge risk={data.delay_risk?.risk} />
      </div>
      
      <div className={compact ? "grid grid-cols-2 gap-2" : "space-y-2"}>
        {data.weather?.description && (
          <div className="flex items-center gap-2 text-sm min-w-0">
            <Cloud className="w-4 h-4 text-white/40" />
            <span className="capitalize truncate">{data.weather.description}</span>
          </div>
        )}
        
        {data.temp && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-2xl font-bold">{Math.round(data.temp)}°F</span>
          </div>
        )}
        
        {data.wind?.speed && (
          <div className="flex items-center gap-2 text-sm text-white/60">
            <Wind className="w-4 h-4" />
            <span>{Math.round(data.wind.speed)} mph</span>
          </div>
        )}
        
        {data.visibility && (
          <div className="flex items-center gap-2 text-sm text-white/60">
            <Eye className="w-4 h-4" />
            <span>{Math.round(data.visibility / 1609)} mi</span>
          </div>
        )}
        
        {!compact && data.delay_risk?.factors?.length > 0 && (
          <div className="pt-2 border-t border-white/10">
            <p className="text-xs text-white/40 mb-1">Risk Factors:</p>
            <div className="flex flex-wrap gap-1">
              {data.delay_risk.factors.map((factor) => (
                <span key={`risk-${factor}`} className="text-xs bg-white/10 px-2 py-0.5 rounded">
                  {factor}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function FlightWeather({ compact = false, splitLayout = false }) {
  const [originSearch, setOriginSearch] = useState("");
  const [destSearch, setDestSearch] = useState("");
  const [flightNumber, setFlightNumber] = useState("");
  const [flightData, setFlightData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Example coordinates (would normally come from airport lookup)
  const airports = {
    "JFK": { lat: 40.6413, lon: -73.7781, name: "New York JFK" },
    "LAX": { lat: 33.9416, lon: -118.4085, name: "Los Angeles" },
    "ORD": { lat: 41.9742, lon: -87.9073, name: "Chicago O'Hare" },
    "ATL": { lat: 33.6407, lon: -84.4277, name: "Atlanta" },
    "DFW": { lat: 32.8998, lon: -97.0403, name: "Dallas/Fort Worth" },
    "DEN": { lat: 39.8561, lon: -104.6737, name: "Denver" },
    "SFO": { lat: 37.6213, lon: -122.379, name: "San Francisco" },
    "MIA": { lat: 25.7959, lon: -80.287, name: "Miami" },
  };
  
  const checkFlight = async () => {
    const origin = airports[originSearch.toUpperCase()];
    const dest = airports[destSearch.toUpperCase()];
    
    if (!origin || !dest) {
      setError("Please enter valid airport codes (e.g., JFK, LAX, ORD)");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.get(`${API}/weather/flight`, {
        params: {
          origin_lat: origin.lat,
          origin_lon: origin.lon,
          dest_lat: dest.lat,
          dest_lon: dest.lon,
          flight_number: flightNumber || undefined
        }
      });
      setFlightData({ ...response.data, originName: origin.name, destName: dest.name });
    } catch (err) {
      setError("Failed to fetch flight weather data");
    } finally {
      setLoading(false);
    }
  };
  
  if (splitLayout) {
    return (
      <div className="grid grid-rows-[minmax(230px,1fr)_minmax(240px,1fr)] gap-4 h-full" data-testid="flight-weather-widget-split">
        <div className="rounded-2xl border border-[#00f2ea]/15 bg-[#00f2ea]/5 p-4 flex flex-col justify-between" data-testid="flight-input-panel">
          <div>
            <p className="text-xs font-semibold text-[#00f2ea]/80 mb-3">Flight lookup</p>
            <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center mb-3">
              <Input
                data-testid="flight-origin-input"
                placeholder="FROM"
                value={originSearch}
                onChange={(e) => setOriginSearch(e.target.value.toUpperCase())}
                className="bg-white/5 border-white/10 text-center uppercase min-w-0"
                maxLength={3}
              />
              <ArrowRight className="w-5 h-5 text-white/40" />
              <Input
                data-testid="flight-destination-input"
                placeholder="TO"
                value={destSearch}
                onChange={(e) => setDestSearch(e.target.value.toUpperCase())}
                className="bg-white/5 border-white/10 text-center uppercase min-w-0"
                maxLength={3}
              />
            </div>
            <Input
              data-testid="flight-number-input"
              placeholder="Flight # optional"
              value={flightNumber}
              onChange={(e) => setFlightNumber(e.target.value.toUpperCase())}
              className="w-full bg-white/5 border-white/10 uppercase mb-3"
            />
            {error && <p className="text-xs text-red-400" data-testid="flight-error-message">{error}</p>}
          </div>
          <div>
            <Button
              data-testid="flight-check-button"
              onClick={checkFlight}
              disabled={loading || !originSearch || !destSearch}
              className="w-full bg-[#00f2ea]/20 hover:bg-[#00f2ea]/30 border border-[#00f2ea]/30"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check Flight Weather"}
            </Button>
            <p className="text-[11px] text-white/40 mt-2" data-testid="flight-supported-airports-note">
              Supported: JFK, LAX, ORD, ATL, DFW, DEN, SFO, MIA
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 overflow-y-auto" data-testid="flight-results-panel">
          {!flightData && !loading && (
            <div className="h-full min-h-[210px] flex flex-col items-center justify-center text-center" data-testid="flight-empty-results-panel">
              <Plane className="w-9 h-9 text-[#00f2ea]/60 mb-3" />
              <p className="text-sm font-semibold text-white/75">Route weather appears here</p>
              <p className="text-xs text-white/45 mt-1 max-w-xs">Enter two airport codes to compare departure, arrival, delay risk, visibility, wind, and en-route conditions.</p>
            </div>
          )}

          {loading && (
            <div className="h-full min-h-[210px] flex items-center justify-center" data-testid="flight-results-loading">
              <Loader2 className="w-6 h-6 animate-spin text-[#00f2ea]" />
            </div>
          )}

          {flightData && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid="flight-rendered-results">
              <div className={`flex items-center gap-3 p-3 rounded-xl ${
                flightData.overall_status === 'good' 
                  ? 'bg-green-500/10 border border-green-500/20' 
                  : 'bg-yellow-500/10 border border-yellow-500/20'
              }`} data-testid="flight-overall-status">
                {flightData.overall_status === 'good' ? <CheckCircle className="w-5 h-5 text-green-400" /> : <AlertTriangle className="w-5 h-5 text-yellow-400" />}
                <span className="font-medium text-sm">
                  {flightData.overall_status === 'good' ? 'Good conditions for your flight' : 'Weather may affect your flight'}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <AirportCard title={`Departure: ${flightData.originName}`} data={flightData.origin} type="origin" compact />
                <AirportCard title={`Arrival: ${flightData.destName}`} data={flightData.destination} type="dest" compact />
              </div>
              {flightData.en_route && (
                <div className="glass-light rounded-xl p-3" data-testid="flight-en-route-card">
                  <div className="flex items-center gap-2 mb-1">
                    <Plane className="w-4 h-4 text-[#00f2ea]" />
                    <span className="text-sm font-medium">En Route</span>
                  </div>
                  <p className="text-sm font-semibold capitalize">{flightData.en_route.turbulence?.level} turbulence</p>
                  <p className="text-xs text-white/55 line-clamp-2">{flightData.en_route.turbulence?.description}</p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? "space-y-3" : "space-y-4"} data-testid="flight-weather-widget">
      {/* Search Form */}
      <div className="flex flex-wrap gap-2 items-center">
        <Input
          data-testid="flight-origin-input"
          placeholder="From (e.g., JFK)"
          value={originSearch}
          onChange={(e) => setOriginSearch(e.target.value.toUpperCase())}
          className="w-24 bg-white/5 border-white/10 text-center uppercase"
          maxLength={3}
        />
        <ArrowRight className="w-5 h-5 text-white/40 self-center" />
        <Input
          data-testid="flight-destination-input"
          placeholder="To (e.g., LAX)"
          value={destSearch}
          onChange={(e) => setDestSearch(e.target.value.toUpperCase())}
          className="w-24 bg-white/5 border-white/10 text-center uppercase"
          maxLength={3}
        />
        <Input
          data-testid="flight-number-input"
          placeholder="Flight # (optional)"
          value={flightNumber}
          onChange={(e) => setFlightNumber(e.target.value.toUpperCase())}
          className="w-32 bg-white/5 border-white/10 uppercase"
        />
        <Button
          data-testid="flight-check-button"
          onClick={checkFlight}
          disabled={loading || !originSearch || !destSearch}
          className="bg-[#00f2ea]/20 hover:bg-[#00f2ea]/30 border border-[#00f2ea]/30"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check"}
        </Button>
      </div>
      
      {error && (
        <p className="text-sm text-red-400">{error}</p>
      )}
      
      {/* Flight Data */}
      {flightData && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={compact ? "space-y-3" : "space-y-4"}
        >
          {/* Overall Status */}
          <div className={`flex items-center gap-3 p-3 rounded-xl ${
            flightData.overall_status === 'good' 
              ? 'bg-green-500/10 border border-green-500/20' 
              : 'bg-yellow-500/10 border border-yellow-500/20'
          }`}>
            {flightData.overall_status === 'good' ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
            )}
            <span className="font-medium">
              {flightData.overall_status === 'good' 
                ? 'Good conditions for your flight' 
                : 'Weather may affect your flight'}
            </span>
          </div>
          
          {/* Airport Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <AirportCard title={`Departure: ${flightData.originName}`} data={flightData.origin} type="origin" compact={compact} />
            <AirportCard title={`Arrival: ${flightData.destName}`} data={flightData.destination} type="dest" compact={compact} />
          </div>
          
          {/* En Route */}
          {flightData.en_route && !compact && (
            <div className="glass-light rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Plane className="w-4 h-4 text-[#00f2ea]" />
                <span className="text-sm font-medium">En Route Conditions</span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold capitalize">
                    {flightData.en_route.turbulence?.level} Turbulence
                  </p>
                  <p className="text-sm text-white/60">
                    {flightData.en_route.turbulence?.description}
                  </p>
                </div>
                {flightData.en_route.weather?.description && (
                  <span className="text-sm text-white/50 capitalize">
                    {flightData.en_route.weather.description}
                  </span>
                )}
              </div>
            </div>
          )}
        </motion.div>
      )}
      
      {/* Help Text */}
      {!flightData && !loading && (
        <p className="text-xs text-white/40" data-testid="flight-supported-airports-note">
          Supported: JFK, LAX, ORD, ATL, DFW, DEN, SFO, MIA
        </p>
      )}
    </div>
  );
}
